import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/context/AuthContext";

/**
 * Voice input hook with Web Speech API primary + Whisper (server) fallback.
 *
 * Usage:
 *   const { supported, recording, transcribing, start, stop, error } =
 *     useVoiceInput({ language: "de-DE", onTranscript: (text, isFinal) => {...} });
 *
 * - Web Speech API streams interim + final results (Chrome/Edge).
 * - Fallback: MediaRecorder captures audio → POST /api/coworker/transcribe → Whisper.
 */
export function useVoiceInput({ language = "de-DE", onTranscript } = {}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // "webspeech" | "whisper" | null

  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const SR = typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  const hasWebSpeech = !!SR;
  const hasMediaRecorder = typeof window !== "undefined" && !!window.MediaRecorder;
  const supported = hasWebSpeech || hasMediaRecorder;

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startWebSpeech = useCallback(() => {
    try {
      const rec = new SR();
      rec.lang = language;
      rec.continuous = false;
      rec.interimResults = true;
      rec.maxAlternatives = 1;

      let finalText = "";
      rec.onresult = (ev) => {
        let interim = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const r = ev.results[i];
          if (r.isFinal) finalText += r[0].transcript;
          else interim += r[0].transcript;
        }
        const combined = (finalText + interim).trim();
        if (combined) onTranscript?.(combined, false);
      };
      rec.onerror = (ev) => {
        setError(ev.error || "speech-error");
        setRecording(false);
        setMode(null);
      };
      rec.onend = () => {
        setRecording(false);
        setMode(null);
        if (finalText.trim()) onTranscript?.(finalText.trim(), true);
      };
      recognitionRef.current = rec;
      rec.start();
      setMode("webspeech");
      setRecording(true);
      setError(null);
    } catch (e) {
      setError(e.message || "webspeech-start-failed");
      setRecording(false);
    }
  }, [SR, language, onTranscript]);

  const startWhisper = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Prefer webm/opus, fall back to any supported type
      let mimeType = "audio/webm;codecs=opus";
      if (!window.MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = window.MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      }
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        cleanupStream();
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        chunksRef.current = [];
        if (blob.size === 0) {
          setError("empty-recording");
          setTranscribing(false);
          return;
        }
        setTranscribing(true);
        try {
          const form = new FormData();
          const ext = (mr.mimeType || "audio/webm").includes("mp4") ? "m4a" : "webm";
          form.append("audio", blob, `recording.${ext}`);
          form.append("language", (language || "de-DE").slice(0, 2));
          form.append("use_profile", "1");
          const res = await api.post("/api/coworker/transcribe", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (res.data?.text || "").trim();
          if (text) onTranscript?.(text, true);
          else setError("empty-transcript");
        } catch (e) {
          setError(e.response?.data?.detail || e.message || "whisper-failed");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setMode("whisper");
      setRecording(true);
      setError(null);
    } catch (e) {
      cleanupStream();
      setError(e.message || "mic-permission-denied");
      setRecording(false);
      setMode(null);
    }
  }, [language, onTranscript, cleanupStream]);

  const start = useCallback(() => {
    if (recording || transcribing) return;
    if (hasWebSpeech) startWebSpeech();
    else if (hasMediaRecorder) startWhisper();
    else setError("not-supported");
  }, [recording, transcribing, hasWebSpeech, hasMediaRecorder, startWebSpeech, startWhisper]);

  const stop = useCallback(() => {
    if (mode === "webspeech" && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
    } else if (mode === "whisper" && mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
      } catch { /* noop */ }
      setRecording(false);
    }
  }, [mode]);

  const toggle = useCallback(() => {
    if (recording) stop(); else start();
  }, [recording, start, stop]);

  useEffect(() => () => {
    // cleanup on unmount
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    try { mediaRecorderRef.current?.state === "recording" && mediaRecorderRef.current.stop(); } catch { /* noop */ }
    cleanupStream();
  }, [cleanupStream]);

  return { supported, recording, transcribing, start, stop, toggle, error, mode };
}
